import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { ROUTE_NAMES } from '@app/core/navigation/navigation.constants';
import { PopupService } from '@app/core/services/popup.service';
import { swapPosition } from '@app/core/utils/helpers';
import { TranslocoService } from '@ngneat/transloco';
import { RxActions, rxActions } from '@rx-angular/state/actions';
import { VesselItinerary } from '@vessel-itinerary/state/itinerary.types';
import { HfoMgoBunker } from '@vessel/vessel/state/vessel.types';
import { catchError, exhaustMap, filter, map, of, switchMap } from 'rxjs';
import { ItineraryService } from './itinerary.service';
import { ItineraryState } from './itinerary.state';

interface IItineraryAction {
  getItineraries: {
    paramMap?: any;
    queryParams?: Params;
  };
  getItinerariesSuccess: void;
  getDetailMovement: {
    id: string;
  };
  getDetailMovementSuccess: {
    movement: VesselItinerary;
  };
  deleteItinerary: {
    id: string | number;
  };
  deleteItinerarySuccess: void;
  changeItineraryPosition: {
    previousIndex: number;
    currentIndex: number;
  };
  updateItinerary: {
    id: string;
    data: any;
  };
  updateItinerarySuccess: {
    itinerary?: VesselItinerary;
  };
  createItinerary: {
    data: any;
  };
  createItinerarySuccess: {
    itinerary?: VesselItinerary;
  };
  getHfoData: void;
  getHfoDataSuccess: {
    data: HfoMgoBunker;
  };
  getMgoBunkerData: void;
  getMgoDataSuccess: {
    data: HfoMgoBunker;
  };
  updateMovementSequences: {
    data: any;
    queryParams?: Params;
  };
  updateMovementSequencesSuccess: {
    data: any;
  };
  getDetailVessel: {
    id: string;
  };
  getAdvanceFilters: {
    key?: 'port_name' | 'agent_company_name' | 'purpose_id';
    limit?: number;
    queryParams?: Params;
  };
  getAdvanceFiltersSuccess: {
    key?: 'port_name' | 'agent_company_name' | 'purpose_id';
    ports?: any[];
    agentCompanies?: any[];
    purposeIds?: any;
  };
  getVesselMovements: {
    vessel_id: number;
  };
  getVesselMovementsSuccess: void;
}

@Injectable({
  providedIn: 'root'
})
export class ItineraryAction {
  protected readonly translocoService: TranslocoService = inject(TranslocoService);
  protected readonly router: Router = inject(Router);
  actions: RxActions<IItineraryAction> = rxActions<IItineraryAction>();
  itineraryService: ItineraryService = inject(ItineraryService);
  state: ItineraryState = inject(ItineraryState);
  popupService: PopupService = inject(PopupService);

  constructor() {
    this._subscribeToGetItineraries$();
    this._subscribeToGetDetailItinerary$();
    this._subscribeToDeleteItinerary$();
    this._subscribeToChangePosition$();
    this._subscribeToCreateItinerary$();
    this._subscribeToUpdateItinerary$();
    this._subscribeToUpdateMovementSequences();
    this._subscribeToGetDetailVessel$();
    this._subscribeToGetAdvanceFilter$();
    this._subscribeToGetAdvanceFilterSuccess$();
    this._subscribeToGetVesselMovements$();
  }

  private _subscribeToGetItineraries$() {
    type APIResponse = UnwrapReturnObs<typeof this.itineraryService.getVesselsItinerary>;
    this.actions.getItineraries$
      .pipe(
        switchMap((payload) =>
          this.itineraryService.getVesselsItinerary(payload?.queryParams).pipe<APIResponse, APIResponse>(
            catchError((error) => of(error)),
            filter((response) => !(response instanceof HttpErrorResponse))
          )
        )
      )
      .subscribe({
        next: ({ meta, data }: any) => {
          this.state.set({
            pagination: meta,
            itineraries: data
          });
        }
      });
  }

  private _subscribeToGetDetailItinerary$() {
    type APIResponse = UnwrapReturnObs<typeof this.itineraryService.getDetailMovement>;
    this.actions.getDetailMovement$
      .pipe(
        exhaustMap((payload) =>
          this.itineraryService.getDetailMovement(payload.id).pipe<APIResponse, APIResponse>(
            catchError((error) => of(error)),
            filter((response) => !(response instanceof HttpErrorResponse))
          )
        )
      )
      .subscribe({
        next: (data) => {
          this.state.set({
            detailItinerary: data.data
          });
          this.actions.getDetailMovementSuccess({ movement: data.data });
        }
      });
  }

  private _subscribeToDeleteItinerary$() {
    this.actions.onDeleteItinerary(
      ($payload) => {
        type APIResponse = UnwrapReturnObs<typeof this.itineraryService.deleteMovement>;
        return $payload.pipe(
          exhaustMap((payload) =>
            this.itineraryService.deleteMovement(payload.id).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      () => {
        this.popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.itinerary_deleted_successfully')
        });
        this.actions.deleteItinerarySuccess();
      }
    );
  }

  private _subscribeToChangePosition$() {
    this.actions.onChangeItineraryPosition(
      (payload$) =>
        payload$.pipe(
          exhaustMap((payload) => {
            const sorted = swapPosition(this.state.itineraries(), payload.previousIndex, payload.currentIndex);
            return of(sorted);
          })
        ),
      (sortedItineraries: VesselItinerary[]) => {
        this.state.set({
          itineraries: sortedItineraries
        });
      }
    );
  }

  private _subscribeToCreateItinerary$() {
    this.actions.onCreateItinerary(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this.itineraryService.createMovement>;
        return payload$.pipe(
          exhaustMap((payload) =>
            this.itineraryService.createMovement(payload.data).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      () => {
        this.popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.itinerary_created_successfully')
        });
        this.actions.createItinerarySuccess({});
      }
    );
  }

  private _subscribeToUpdateItinerary$() {
    this.actions.onUpdateItinerary(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this.itineraryService.updateMovement>;
        return payload$.pipe(
          exhaustMap((payload) =>
            this.itineraryService.updateMovement(payload.id, payload.data).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        this.popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.itinerary_updated_successfully')
        });
        this.state.set({ detailItinerary: response?.data });
        this.actions.updateItinerarySuccess({});
      }
    );
  }

  private _subscribeToUpdateMovementSequences() {
    this.actions.onUpdateMovementSequences(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this.itineraryService.updateMovementSequences>;
        return payload$.pipe(
          exhaustMap((payload) =>
            this.itineraryService.updateMovementSequences(payload.data).pipe<APIResponse, APIResponse, any>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse)),
              map((response) => ({
                response,
                queryParams: payload.queryParams
              }))
            )
          )
        );
      },
      (response: any) => {
        this.popupService.messageSnackbar({
          type: 'success',
          message: this.translocoService.translate('ship_interface.movement_updated_successfully')
        });

        if (!this.router.url.includes(`${ROUTE_NAMES.VOYAGE_CHARTER}/${ROUTE_NAMES.VOYAGE_MANAGEMENT}`)) {
          this.actions.getItineraries({ queryParams: response.queryParams });
        }
        this.actions.updateMovementSequencesSuccess({ data: response });
      }
    );
  }

  private _subscribeToGetDetailVessel$() {
    type APIResponse = UnwrapReturnObs<typeof this.itineraryService.getDetailVessel>;
    this.actions.getDetailVessel$
      .pipe(
        exhaustMap((payload) =>
          this.itineraryService.getDetailVessel(payload.id).pipe<APIResponse, APIResponse>(
            catchError((error) => of(error)),
            filter((response) => !(response instanceof HttpErrorResponse))
          )
        )
      )
      .subscribe({
        next: (data) => {
          this.state.set({
            detailVessel: data.data
          });
        }
      });
  }

  private _subscribeToGetAdvanceFilter$(): void {
    this.actions.onGetAdvanceFilters(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this.itineraryService.getAdvanceFilters>;

        return payload$.pipe(
          exhaustMap((payload) =>
            this.itineraryService.getAdvanceFilters(payload.key, payload.limit).pipe<APIResponse, APIResponse, any>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse)),
              map((response) => ({ payload, response })) // Return both payload and response
            )
          )
        );
      },
      ({ response, payload }: any) => {
        if (payload.key === 'port_name') {
          this.actions.getAdvanceFiltersSuccess({ ports: response.data, key: payload.key });
        } else if (payload.key === 'agent_company_name') {
          this.actions.getAdvanceFiltersSuccess({ agentCompanies: response.data, key: payload.key });
        } else if (payload.key === 'purpose_id') {
          this.actions.getAdvanceFiltersSuccess({ purposeIds: response.data, key: payload.key });
        }
      }
    );
  }

  private _subscribeToGetAdvanceFilterSuccess$(): void {
    this.actions.getAdvanceFiltersSuccess$.subscribe({
      next: ({ ports, agentCompanies, purposeIds, key }: any) => {
        if (key === 'port_name') {
          this.state.set({
            ports
          });
        } else if (key === 'agent_company_name') {
          this.state.set({
            agentCompanies
          });
        } else if (key === 'purpose_id') {
          this.state.set({
            purposeIds
          });
        }
      }
    });
  }

  private _subscribeToGetVesselMovements$() {
    this.actions.onGetVesselMovements(
      (payload$) => {
        type APIResponse = UnwrapReturnObs<typeof this.itineraryService.getVesselMovements>;
        return payload$.pipe(
          exhaustMap((payload) =>
            this.itineraryService.getVesselMovements(payload.vessel_id, {}).pipe<APIResponse, APIResponse>(
              catchError((error) => of(error)),
              filter((response) => !(response instanceof HttpErrorResponse))
            )
          )
        );
      },
      (response: any) => {
        if (response?.error) {
          return;
        }
        this.state.set({
          vesselMovements: response?.data
        });
        this.actions.getVesselMovementsSuccess();
      }
    );
  }
}
